import { PercentPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input, OnDestroy, OnInit, viewChild, ViewEncapsulation } from '@angular/core';
import { MatDrawer, MatDrawerContainer, MatDrawerContent } from '@angular/material/sidenav';
import { ProjectAssetMappingService } from '@app/cem/project/content/project-asset-mapping/project-asset-mapping.service';
import { ProjectAssetMappingBulkService } from '@app/cem/project/services/project-asset-mapping-bulk.service';
import { ErrorService } from '@app/common/services/error.service';
import { PxdAgGridOptions } from '@app/shared/aggrid/pxd-ag-grid-options';
import { AssetsProjectAssetMappingView } from '@app/shared/models/assets-project-asset-mapping-view';
import { calculateSplitPercentages, round } from '@app/shared/utilities/math-utilities';
import { ButtonDirective } from '@progress/kendo-angular-buttons';
import { AgGridAngular } from 'ag-grid-angular';
import { CellValueChangedEvent, GridReadyEvent, SelectionChangedEvent } from 'ag-grid-community';
import { AssetMappingConfiguration, IModuleAllocation } from 'app/cem/project/models/asset-mapping-configuration';
import { Project, ProjectAssetMapping } from 'app/shared/models';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { assetMappingMultiAssetAvailablesGridOptions, assetMappingMultiAssetMappedGridOptions } from './multi-asset-grid-options';

@Component({
  selector: 'app-project-asset-mapping-multi-asset',
  templateUrl: './project-asset-mapping-multi-asset.component.html',
  styleUrls: ['./project-asset-mapping-multi-asset.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AgGridAngular,
    ButtonDirective,
    MatDrawer,
    MatDrawerContainer,
    MatDrawerContent,
    PercentPipe,
  ],
})
export class ProjectAssetMappingMultiAssetComponent implements OnInit, OnDestroy {
  private errorService = inject(ErrorService);
  private bulkAssetMappingService = inject(ProjectAssetMappingBulkService);

  private projectTotalAllocationSubscription: Subscription;
  private modulesToReloadSubscription: Subscription;
  private modulesToSaveChangesSubscription: Subscription;
  private modulestoCancelChangesSubscription: Subscription;
  private totalAllocationSubscription: Subscription;
  private mappedAssetsChangedSubscription: Subscription;
  private totalAllocation: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public totalAllocationPercentage: number = 0;
  private hasPendingChange: boolean = false;
  private initialLoad: boolean = true;
  public showToggleAllocation: boolean;
  public totalMappedAssets: number;
  private totalProjectAllocationPercentage: number;
  public allocationAllowed: boolean = false;
  public moduleIsEditable: boolean = false;
  private newAssetId: number;

  readonly project = input<Project>(undefined);
  readonly projectId = input<number>(undefined);
  readonly assetMappingConfig = input<AssetMappingConfiguration>(undefined);
  readonly projectAssetMappingService = input<ProjectAssetMappingService>(undefined);

  private readonly agAvailablesGrid = viewChild<AgGridAngular>("availablesGrid");
  private readonly agMappedGrid = viewChild<AgGridAngular>("mappedGrid");

  availablesGridOptions: PxdAgGridOptions = assetMappingMultiAssetAvailablesGridOptions;
  mappedGridOptions: PxdAgGridOptions = assetMappingMultiAssetMappedGridOptions;

  availableAssets: AssetsProjectAssetMappingView[] = [];
  mappedAssetsChanged$: Subject<boolean> = new Subject<boolean>();
  mappedAssets: ProjectAssetMapping[] = [];

  editedMappedAssets: Map<number, ProjectAssetMapping> = new Map<number, ProjectAssetMapping>();
  deletedMappedAssets: Map<number, ProjectAssetMapping> = new Map<number, ProjectAssetMapping>();

  initialMappedAssetsLoaded: boolean = false;
  initialAvailableAssetsLoaded: boolean = false;
  mappedAssetsGridReady: boolean = false;
  availableAssetsGridReady: boolean = false;
  disableAddSelected: boolean = true;
  disableRemoveMapped: boolean = true;
  disableAllocateEvenly: boolean = true;

  async ngOnInit() {
    const assetMappingConfig = this.assetMappingConfig();
    this.moduleIsEditable = assetMappingConfig.ModuleProperties.IsMappingEditable && assetMappingConfig.ModuleProperties.IsMappingEditable;
    const assetMappingConfigValue = this.assetMappingConfig();
    this.allocationAllowed = assetMappingConfigValue.IsAllowAllocation && assetMappingConfigValue.IsAllowAllocation;
    this.setupSubscriptions();
    await Promise.all([this.getAvailableAssetsAndTryLoadGrid(), this.getMappedAssetsAndTryLoadGrid()]);
  }

  ngOnDestroy() {
    this.projectTotalAllocationSubscription.unsubscribe();
    this.modulesToReloadSubscription.unsubscribe();
    this.modulesToSaveChangesSubscription.unsubscribe();
    this.modulestoCancelChangesSubscription.unsubscribe();
    this.totalAllocationSubscription.unsubscribe();
    this.mappedAssetsChangedSubscription.unsubscribe();
  }

  private setupSubscriptions() {
    this.totalAllocationSubscription = this.totalAllocation.subscribe(t => {
      let allocationPercentage = this.totalAllocation.getValue();
      this.totalAllocationPercentage = allocationPercentage;
      this.setToggleModule(this.totalMappedAssets > 0 ? true : false);
      this.projectAssetMappingService().setModuleAllocation(
        {
          assetMappingConfigurationId: this.assetMappingConfig().AssetMappingConfigurationId,
          moduleId: this.assetMappingConfig().ModuleId,
          hasAssets: this.totalMappedAssets > 0 ? true : false,
          isActiveToggle: this.totalMappedAssets > 0 ? true : false,
          allocationPercentage: allocationPercentage || 0,
          isToggle: this.assetMappingConfig().ModuleProperties.IsToggleModule,
          hasPendingChange: this.hasPendingChange
        }
      );
    });

    this.mappedAssetsChangedSubscription = this.mappedAssetsChanged$.subscribe(() => {
      if (this.mappedAssets.length > 0 || this.deletedMappedAssets.size > 0) {
        this.disableAllocateEvenly = false;
        let currentTotalMappedAsset = this.totalMappedAssets;
        let currentTotalAllocation = this.totalAllocation.getValue();
        let allocation = 0;
        this.mappedAssets.forEach(a => { allocation += a.PercentAllocation });
        this.totalMappedAssets = this.mappedAssets.length;
        if ((currentTotalAllocation !== allocation || currentTotalMappedAsset !== this.totalMappedAssets) && this.initialLoad === false) {
          this.hasPendingChange = true;
        }

        this.initialLoad = false;
        this.totalAllocation.next(allocation);
        this.projectAssetMappingService().setModuleAllocation(
          {
            assetMappingConfigurationId: this.assetMappingConfig().AssetMappingConfigurationId,
            moduleId: this.assetMappingConfig().ModuleId,
            allocationPercentage: allocation || 0,
            hasAssets: true,
            isActiveToggle: true,
            isToggle: this.assetMappingConfig().ModuleProperties.IsToggleModule,
            hasPendingChange: this.hasPendingChange
          });
      } else {
        this.disableAllocateEvenly = true;
      }
    });

    this.modulesToReloadSubscription = this.projectAssetMappingService().getModulesToReload()
      .subscribe((event) => {
        event.map(async (data) => {
          const assetMappingConfig = this.assetMappingConfig();
          if (data && assetMappingConfig.AssetMappingConfigurationId && data.assetMappingConfigurationId === assetMappingConfig.AssetMappingConfigurationId) {
            await this.clearAllocation(data);
          }
        });
      });

    this.modulesToSaveChangesSubscription = this.projectAssetMappingService().getModulesToSaveChanges()
      .subscribe((event) => {
        event.map(async (data) => {
          const assetMappingConfig = this.assetMappingConfig();
          if (data && assetMappingConfig.AssetMappingConfigurationId && data.assetMappingConfigurationId === assetMappingConfig.AssetMappingConfigurationId) {
            await this.saveProjectAssetMapping();
          }
        });
      });

    this.modulestoCancelChangesSubscription = this.projectAssetMappingService().getModulesToCancelChanges()
      .subscribe((event) => {
        event.map(async (data) => {
          const assetMappingConfig = this.assetMappingConfig();
          if (data && assetMappingConfig.AssetMappingConfigurationId && data.assetMappingConfigurationId === assetMappingConfig.AssetMappingConfigurationId) {
            await this.cancelChanges();
          }
        });
      });

    this.projectTotalAllocationSubscription = this.projectAssetMappingService().getProjectTotalAllocation().subscribe(x => {
      this.totalProjectAllocationPercentage = x;
    });
  }

  private async clearAllocation(module: IModuleAllocation) {
    this.hasPendingChange = false;
    await Promise.all([this.getAvailableAssetsAndTryLoadGrid(), this.getMappedAssetsAndTryLoadGrid()]);
    this.showToggleAllocation = true;
    this.projectAssetMappingService().setModuleAllocation(module);
  }

  private setToggleModule(hasAsset: boolean) {
    if (this.assetMappingConfig().ModuleProperties.IsToggleModule) {
      this.showToggleAllocation = !hasAsset;
    }
  }

  private async saveProjectAssetMapping() {
    try {
      let toDeleteArray = Array.from(this.deletedMappedAssets.values());
      let toUpdateArray = Array.from(this.editedMappedAssets.values());
      let toCreateArray = this.mappedAssets
        .filter(x => x.ProjectAssetMappingId <= 0)
        .map(x => {
          x.ProjectAssetMappingId = 0;
          return x;
        });
      await this.bulkAssetMappingService.saveChanges$(toDeleteArray, toUpdateArray, [], toCreateArray).toPromise();
    } catch (err) {
      this.errorService.error("There was an error saving AssetAllocation for Multi Assets", err);
    }
    this.hasPendingChange = false;
    await Promise.all([this.getAvailableAssetsAndTryLoadGrid(), this.getMappedAssetsAndTryLoadGrid()]);
  }

  private async cancelChanges() {
    this.agAvailablesGrid().api.deselectAll();
    this.agMappedGrid().api.deselectAll();

    this.deletedMappedAssets = new Map<number, ProjectAssetMapping>();
    this.editedMappedAssets = new Map<number, ProjectAssetMapping>();

    this.hasPendingChange = false;
    await Promise.all([this.getAvailableAssetsAndTryLoadGrid(), this.getMappedAssetsAndTryLoadGrid()]);
  }

  cancelAvailableAssetsSelection() {
    this.agAvailablesGrid().api.deselectAll();
  }

  removeMappedAssets() {
    let rowsToRemove = this.agMappedGrid().api.getSelectedRows() as ProjectAssetMapping[];
    rowsToRemove.filter(x => x.ProjectAssetMappingId > 0).forEach(x => {
      this.deletedMappedAssets.set(x.ProjectAssetMappingId, x);
      this.editedMappedAssets.delete(x.ProjectAssetMappingId)
    });
    this.mappedAssets = this.mappedAssets.filter(x => !rowsToRemove.includes(x));
    this.agMappedGrid().api.applyTransaction({ remove: rowsToRemove });
    this.agAvailablesGrid().api.applyTransaction({ add: rowsToRemove });
    this.hasPendingChange = true;
    this.mappedAssetsChanged$.next(true);
  }

  splitAllocation() {
    let totalAllocationPct = this.totalAllocation.getValue();
    let pct = round(this.totalProjectAllocationPercentage - totalAllocationPct, 6);
    let modulePercentage = 1 - pct;
    if (modulePercentage < 0) {
      return;
    }

    let splitPercentages = calculateSplitPercentages(modulePercentage, this.totalMappedAssets, 6);
    let allocation = 0;
    let changes = this.mappedAssets.map((x, ix) => {
      x.PercentAllocation = splitPercentages[ix];
      allocation = allocation + x.PercentAllocation;
      if (x.ProjectAssetMappingId > 0) {
        this.editedMappedAssets.set(x.ProjectAssetMappingId, x);
      }
      return x;
    });
    this.agMappedGrid().api.applyTransaction({ update: changes })
    this.hasPendingChange = true;
    this.totalAllocation.next(allocation);
  }

  public toggleAllocationModule() {
    this.showToggleAllocation = false;
    this.projectAssetMappingService().toggleModuleAllocation(this.projectId(), this.assetMappingConfig().AssetMappingConfigurationId);
  }

  public addAssetToMapping() {
    let assetsToAdd: ProjectAssetMapping[] = [];
    let selectedAvailableAssets = this.agAvailablesGrid().api.getSelectedRows() as AssetsProjectAssetMappingView[];
    if (selectedAvailableAssets.length > 0) {
      selectedAvailableAssets.forEach(item => {
        let newMapping = new ProjectAssetMapping();
        newMapping.AssetId = item.AssetId;
        newMapping.ProjectAssetMappingId = this.newAssetId--;
        newMapping.ProjectId = this.projectId();
        const assetMappingConfig = this.assetMappingConfig();
        newMapping.IsConstructedAsset = assetMappingConfig.IsConstructedModule && assetMappingConfig.IsConstructedModule;
        newMapping.PercentAllocation = 0;

        assetsToAdd.push(newMapping);
      });

      this.agMappedGrid().api.applyTransaction({ add: assetsToAdd });
      this.mappedAssets = this.mappedAssets.concat(assetsToAdd);
      this.hasPendingChange = true;
      this.mappedAssetsChanged$.next(true);
      this.agAvailablesGrid().api.applyTransaction({ remove: selectedAvailableAssets });
      this.agAvailablesGrid().api.deselectAll();
    }
  }

  onAvailablesSelectionChanged(params: SelectionChangedEvent) {
    if (this.agAvailablesGrid().api.getSelectedRows().length > 0) {
      this.disableAddSelected = false;
    } else {
      this.disableAddSelected = true;
    }
  }

  onMappedCellValueChanged(params: CellValueChangedEvent): void {
    if (params.oldValue === params.newValue) {
      return;
    }
    let delta = params.newValue - params.oldValue;
    let allocation = this.totalAllocation.getValue();
    this.hasPendingChange = true;
    this.totalAllocation.next(allocation + delta);
    if (params.data.ProjectAssetMappingId > 0) {
      this.editedMappedAssets.set(params.data.ProjectAssetMappingId, params.data);
    }
  }

  onMappedSelectionChanged(params: SelectionChangedEvent) {
    if (this.agMappedGrid().api.getSelectedRows().length > 0) {
      this.disableRemoveMapped = false;
    } else {
      this.disableRemoveMapped = true;
    }
  }

  async getMappedAssetsAndTryLoadGrid() {
    try {
      this.mappedAssets = await this.projectAssetMappingService().findMappedAssets(this.projectId(), this.assetMappingConfig().AssetMappingConfigurationId);
      this.initialMappedAssetsLoaded = true;
      this.hasPendingChange = false;
      this.mappedAssetsChanged$.next(true);
      this.loadMappedGrid();
    } catch (error) {
      this.errorService.handleHTTPErrorWithMessage(error.error, "There was an error getting ProjectAssetMappings for Asset Allocation");
    }
  }

  async onMappedGridReady(params: GridReadyEvent): Promise<void> {
    this.mappedAssetsGridReady = true;
    this.loadMappedGrid();
  }

  loadMappedGrid() {
    const agMappedGrid = this.agMappedGrid();
    if (this.initialMappedAssetsLoaded && this.mappedAssetsGridReady
      && agMappedGrid?.api) {
      this.newAssetId = 0;
      agMappedGrid.api.setGridOption('rowData', this.mappedAssets);
    }
  }

  async getAvailableAssetsAndTryLoadGrid() {
    try {
      this.availableAssets = await this.projectAssetMappingService().findAvailableAssets(this.projectId(), this.assetMappingConfig().AssetMappingConfigurationId);
      this.initialAvailableAssetsLoaded = true;
      this.loadAvailablesGrid();
    } catch (error) {
      this.errorService.handleHTTPErrorWithMessage(error.error, "There was an error getting Asset Options for Asset Allocation");
    }
  }

  async onAvailablesGridReady(params: GridReadyEvent): Promise<void> {
    this.availableAssetsGridReady = true;
    this.loadAvailablesGrid();
  }

  loadAvailablesGrid() {
    const agAvailablesGrid = this.agAvailablesGrid();
    if (this.initialAvailableAssetsLoaded && this.availableAssetsGridReady
      && agAvailablesGrid?.api) {
      agAvailablesGrid.api.setGridOption('rowData', this.availableAssets);
    }
  }
}